import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Calendar, User, ArrowRight, Tag, Images } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { API } from "@/App";
import { SEO } from "@/components/SEO";

const categories = [
  "All",
  "Microgreens Nutrition",
  "Healthy Recipes",
  "Urban Farming",
  "Wellness Tips"
];

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog?limit=20`);
        setPosts(response.data);
        setFilteredPosts(response.data);
      } catch (error) {
        console.error("Failed to fetch blog posts:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  useEffect(() => {
    let filtered = posts;

    if (selectedCategory !== "All") {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        p =>
          p.title.toLowerCase().includes(query) ||
          p.excerpt.toLowerCase().includes(query) ||
          p.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    setFilteredPosts(filtered);
  }, [searchQuery, selectedCategory, posts]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="pt-16 md:pt-20 lg:pt-24" data-testid="blog-page">
      <SEO title="Blog" description="Read the latest articles about microgreens, nutrition, recipes, and sustainable farming from Praroha Greens." keywords="microgreens blog, nutrition articles, healthy recipes" path="/blog" />
      {/* Hero */}
      <section className="py-8 md:py-10 bg-praroha-forest">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
            <span className="inline-block px-4 py-1.5 bg-white/10 text-white/90 text-sm font-medium rounded-full mb-3">
              Our Blog
            </span>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
              Health & Wellness Insights
            </h1>
            <p className="text-white/50 text-sm">
              Discover the latest articles on microgreens nutrition, healthy recipes, urban farming tips, and wellness advice.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 border-b border-border/50 sticky top-16 md:top-20 bg-white/95 backdrop-blur-md z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="blog-search-input"
              />
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
              {categories.map((cat) => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(cat)}
                  className={`rounded-full ${
                    selectedCategory === cat
                      ? "bg-praroha-forest text-white"
                      : "border-praroha-forest/30 text-praroha-forest hover:bg-praroha-forest/5"
                  }`}
                  data-testid={`blog-category-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {cat}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-8 md:py-10 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-96 bg-muted animate-pulse rounded-2xl" />
              ))}
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground text-lg">No articles found matching your criteria.</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => {
                  setSearchQuery("");
                  setSelectedCategory("All");
                }}
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => {
                const extraPhotoCount = (post.images?.length || 0) > 1 ? post.images.length - 1 : 0;
                return (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link to={`/blog/${post.slug}`} data-testid={`blog-post-${post.slug}`}>
                      <Card className="group h-full overflow-hidden border-border/40 hover:shadow-hover transition-all duration-500">
                        <div className="relative aspect-video overflow-hidden">
                          <img
                            src={post.image}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          {extraPhotoCount > 0 && (
                            <span
                              className="absolute bottom-2 right-2 inline-flex items-center gap-1 bg-black/60 text-white text-xs font-medium px-2 py-1 rounded-full"
                              data-testid={`blog-post-photo-count-${post.slug}`}
                            >
                              <Images className="w-3 h-3" />
                              +{extraPhotoCount}
                            </span>
                          )}
                        </div>
                        <CardContent className="p-6">
                          <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                            <span className="inline-flex items-center gap-1">
                              <Tag className="w-3 h-3" />
                              {post.category}
                            </span>
                            <span className="inline-flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(post.created_at)}
                            </span>
                          </div>
                          <h2 className="font-heading text-xl font-semibold text-praroha-forest mb-3 group-hover:text-praroha-sage transition-colors line-clamp-2">
                            {post.title}
                          </h2>
                          <p className="text-muted-foreground text-sm line-clamp-3 mb-4">
                            {post.excerpt}
                          </p>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {post.author}
                            </span>
                            <span className="text-praroha-forest text-sm font-medium group-hover:text-praroha-sage transition-colors inline-flex items-center gap-1">
                              Read More
                              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-10 md:py-12 bg-praroha-forest text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-4">
            Stay Updated
          </h2>
          <p className="text-white/80 mb-8">
            Subscribe to our newsletter for the latest articles, recipes, and wellness tips.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="Enter your email"
              className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/50"
            />
            <Button className="bg-white text-praroha-sage hover:bg-white/90 rounded-full px-6">
              Subscribe
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}